import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  FormControlLabel,
  Switch,
  FormLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
} from '@mui/material';

const CANADIAN_PROVINCES = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Nova Scotia',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Northwest Territories',
  'Nunavut',
  'Yukon',
];

// Format phone number as user types: (XXX) XXX-XXXX
const formatPhoneNumber = (input: string): string => {
  const digitsOnly = input.replace(/\D/g, '');
  if (digitsOnly.length === 0) {
    return '';
  } else if (digitsOnly.length <= 3) {
    return `(${digitsOnly}`;
  } else if (digitsOnly.length <= 6) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
  } else {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
  }
};

interface CompanyProfileStepProps {
  onNext: (data: any) => void;
  onBack: () => void;
  existingData?: any;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

export const CompanyProfileStep: React.FC<CompanyProfileStepProps> = ({
  onNext,
  onBack,
  existingData = {},
  isFirstStep = false,
  isLastStep = false,
}) => {
  const [companyName, setCompanyName] = useState(existingData?.companyName || '');
  const [website, setWebsite] = useState(existingData?.website || '');
  const [linkToAccountPhone, setLinkToAccountPhone] = useState(existingData?.linkToAccountPhone ?? false);
  const [phone, setPhone] = useState(existingData?.phone || '');
  const [hasIftaLicence, setHasIftaLicence] = useState(existingData?.hasIftaLicence ?? false);
  const [iftaLicenceOrigin, setIftaLicenceOrigin] = useState(existingData?.iftaLicenceOrigin || '');
  const [iftaLicenceNumber, setIftaLicenceNumber] = useState(existingData?.iftaLicenceNumber || '');
  const [iftaLicenceExpiryDate, setIftaLicenceExpiryDate] = useState(existingData?.iftaLicenceExpiryDate || '');
  const [facebookUrl, setFacebookUrl] = useState(existingData?.facebookUrl || '');
  const [twitterUrl, setTwitterUrl] = useState(existingData?.twitterUrl || '');
  const [linkedInUrl, setLinkedInUrl] = useState(existingData?.linkedInUrl || '');
  const [error, setError] = useState('');

  useEffect(() => {
    // Load company name from user data if available
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user && !companyName) {
      // Try to get company name from registration data or user object
      const registrationData = JSON.parse(localStorage.getItem('registrationData') || '{}');
      if (registrationData.companyName) {
        setCompanyName(registrationData.companyName);
      }
    }
  }, []);

  // Get account phone from contact info step
  const getAccountPhone = () => {
    const contactData = existingData?.contactData || 
      (() => {
        try {
          const stepData = JSON.parse(localStorage.getItem('setupWizardData') || '{}');
          return stepData.contact;
        } catch {
          return null;
        }
      })();
    
    if (contactData && contactData.phoneNumber) {
      return contactData.phoneNumber;
    }
    return null;
  };

  const accountPhone = getAccountPhone();

  const handleSubmit = () => {
    setError('');

    // Validation - company name is required
    if (!companyName.trim()) {
      setError('Company name is required');
      return;
    }

    // If not linking to account phone, validate phone
    if (!linkToAccountPhone && !phone.trim()) {
      setError('Phone number is required when not linking to account phone');
      return;
    }

    // Validate IFTA fields if hasIftaLicence is true
    if (hasIftaLicence) {
      if (!iftaLicenceOrigin.trim()) {
        setError('IFTA licence origin (province/state) is required');
        return;
      }
      if (!iftaLicenceNumber.trim()) {
        setError('IFTA licence number is required');
        return;
      }
      if (!iftaLicenceExpiryDate.trim()) {
        setError('IFTA licence expiry date is required');
        return;
      }
    }

    const data = {
      companyName: companyName.trim(),
      website: website.trim() || undefined,
      linkToAccountPhone,
      phone: linkToAccountPhone ? undefined : phone.trim() || undefined,
      hasIftaLicence: hasIftaLicence || undefined,
      iftaLicenceOrigin: hasIftaLicence ? iftaLicenceOrigin.trim() || undefined : undefined,
      iftaLicenceNumber: hasIftaLicence ? iftaLicenceNumber.trim() || undefined : undefined,
      iftaLicenceExpiryDate: hasIftaLicence ? iftaLicenceExpiryDate.trim() || undefined : undefined,
      facebookUrl: facebookUrl.trim() || undefined,
      twitterUrl: twitterUrl.trim() || undefined,
      linkedInUrl: linkedInUrl.trim() || undefined,
    };

    onNext(data);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Let's set up your company profile. You can update this information later if needed.
      </Typography>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Company Name */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Company Name *</Typography>
          <TextField
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            required
            fullWidth
            placeholder="Enter your company name"
          />
        </Box>

        {/* Website */}
        <Box>
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Website (Optional)</Typography>
          <TextField
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            fullWidth
            placeholder="https://www.example.com"
          />
        </Box>

        {/* Phone Section */}
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Company Phone</Typography>
          
          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
            Link your company phone number to your account phone?
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Maintain one phone number instead of two. If the phone number you defined in your DriverVerified account details is the same as the phone number you publicly use with your Company business, you can link them so they are always identical.
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup
              value={linkToAccountPhone ? 'yes' : 'no'}
              onChange={(e) => setLinkToAccountPhone(e.target.value === 'yes')}
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">
                      Yes - Use account primary phone number
                    </Typography>
                    {accountPhone && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        {accountPhone}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label="No - Use a different company phone number"
                sx={{ mt: 1 }}
              />
            </RadioGroup>
          </FormControl>

          {!linkToAccountPhone && (
            <Box>
              <TextField
                label="Phone Number"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                onKeyDown={(e) => {
                  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                  if (allowedKeys.includes(e.key)) return;
                  if (!/^\d$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                required
                fullWidth
                placeholder="(XXX) XXX-XXXX"
                inputProps={{
                  maxLength: 14,
                }}
              />
            </Box>
          )}
        </Box>

        {/* IFTA Licence */}
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>IFTA Licence (Optional)</Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={hasIftaLicence}
                onChange={(e) => setHasIftaLicence(e.target.checked)}
              />
            }
            label="Do you have an IFTA licence?"
            sx={{ mb: 2 }}
          />

          {hasIftaLicence && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth required>
                <Typography sx={{ fontWeight: 'bold', mb: 1 }}>IFTA Licence Origin (Province/State)</Typography>
                <Select
                  value={iftaLicenceOrigin}
                  onChange={(e) => setIftaLicenceOrigin(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">Select Province/State ...</MenuItem>
                  {CANADIAN_PROVINCES.map((prov) => (
                    <MenuItem key={prov} value={prov}>
                      {prov}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="IFTA Licence Number"
                value={iftaLicenceNumber}
                onChange={(e) => setIftaLicenceNumber(e.target.value)}
                required
                fullWidth
                placeholder="Enter licence number"
              />
              <TextField
                label="IFTA Licence Expiry Date"
                type="date"
                value={iftaLicenceExpiryDate}
                onChange={(e) => setIftaLicenceExpiryDate(e.target.value)}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </Box>

        {/* Social Media */}
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>Social Media (Optional)</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Facebook URL"
              type="url"
              value={facebookUrl}
              onChange={(e) => setFacebookUrl(e.target.value)}
              fullWidth
              placeholder="https://www.facebook.com/yourcompany"
            />
            <TextField
              label="Twitter URL"
              type="url"
              value={twitterUrl}
              onChange={(e) => setTwitterUrl(e.target.value)}
              fullWidth
              placeholder="https://twitter.com/yourcompany"
            />
            <TextField
              label="LinkedIn URL"
              type="url"
              value={linkedInUrl}
              onChange={(e) => setLinkedInUrl(e.target.value)}
              fullWidth
              placeholder="https://www.linkedin.com/company/yourcompany"
            />
          </Box>
        </Box>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={isFirstStep}
          sx={{ minWidth: 120 }}
        >
          Previous Step
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ minWidth: 120 }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

